/* ============================================================
   S.A.D CLOTHING — storefront behaviour
   Loaded with `defer` from layout/theme.liquid.
   Interactive behaviour (cart drawer, filters, gallery) is added
   by the sections that need it.
   ============================================================ */

/* ---------- Collection / search toolbar ----------------------------------
   Two hooks only: the sort select re-submits the GET form (so the choice
   lands in the URL, keeping any active filters), and the native <details>
   filter drawer closes on Escape or on a click outside it. Without JS the
   drawer still opens and the form still submits through its Apply button. */
(function () {
  var form = document.querySelector("[data-collection-form]");
  if (!form) return;

  form.addEventListener("submit", function () {
    var numbers = form.querySelectorAll('input[type="number"]');
    Array.prototype.forEach.call(numbers, function (input) {
      if (input.value === "") input.disabled = true;
    });
  });

  var sort = form.querySelector("[data-sort-select]");
  if (sort) {
    sort.addEventListener("change", function () {
      if (form.requestSubmit) {
        form.requestSubmit();
      } else {
        form.submit();
      }
    });
  }

  var drawer = form.querySelector("[data-filter-drawer]");
  if (!drawer) return;

  document.addEventListener("click", function (event) {
    if (drawer.open && !drawer.contains(event.target)) drawer.open = false;
  });

  document.addEventListener("keydown", function (event) {
    if (event.key !== "Escape" || !drawer.open) return;
    drawer.open = false;
    var toggle = drawer.querySelector("summary");
    if (toggle) toggle.focus();
  });
})();

/* ---------- Product page + cart drawer -----------------------------------
   window.SAD — the product gallery, the variant picker, AJAX add-to-cart and
   the cart drawer. Routes and the one localised string the drawer has to
   rebuild come from sections/cart-drawer.liquid (window.SADroutes /
   window.SADstrings), which the layout renders on every page.

   Everything degrades: without JS the size buttons are inert but the product
   form still posts to /cart/add, the header pill is a link to /cart, and the
   cart page does the same work as the drawer. */
window.SAD = (() => {
  const $ = (s, r = document) => r.querySelector(s);
  const $$ = (s, r = document) => [...r.querySelectorAll(s)];

  const routes = () =>
    window.SADroutes || { add: "/cart/add", cart: "/cart", change: "/cart/change" };
  const strings = () =>
    window.SADstrings || {
      cartPill: "Cart (__COUNT__)",
      addError: "Could not add to cart",
      changeError: "Could not update the cart",
    };

  /* Fallback only — the server-rendered price strings in [data-product-json]
     come from the `money` filter, so they follow the store's currency format. */
  const money = (cents) =>
    "S$" + (cents / 100).toLocaleString("en-SG", { minimumFractionDigits: 0, maximumFractionDigits: 2 });

  /* Re-invocation guard. The theme editor re-renders one section at a time and
     `shopify:section:load` re-runs every binder below; without this a second
     pass over the same element would add a second arrow handler, and one press
     would step two photos. A re-rendered section is a *new* element, so a
     genuine re-render still binds. */
  function bindOnce(root, flag) {
    if (!root || !root.dataset || root.dataset[flag] === "1") return false;
    root.dataset[flag] = "1";
    return true;
  }

  /* ---------- gallery ----------
     The thumbs strip, plus the media box's own two arrows (Task 17). One
     `show()` owns "which photo is on screen": it moves the lead `src`, the
     thumb's active ring and `data-zoom-index` together, and `step()` wraps
     round it (last -> first, first -> last).

     It is the only such owner. The viewer is a separate module with an index of
     its own, and it announces every move back here as a `sad:goto` event rather
     than keeping a second copy — otherwise stepping inside the viewer and
     closing it would leave the media box on the photo the shopper started from.

     A finger can either step or open the viewer, so the two are told apart by
     movement: past SWIPE_MIN the gesture is a swipe and the click it produces
     is dropped, under it the tap reaches the viewer. `data-gallery-dragged` is
     how that reaches the zoom module, which owns the click handler. */
  const SWIPE_MIN = 30;

  function gallery(root) {
    const main = $("[data-main-image]", root);
    if (!main) return;
    if (!bindOnce(root, "galleryBound")) return;
    const thumbs = $$("[data-thumb]", root);
    const total = thumbs.length;
    /* Task 18: the strip's own micro counter, and the box that scrolls when
       there are more thumbs than fit. Both are absent on a one-photo product,
       whose whole strip is never rendered. */
    const counter = $("[data-gallery-counter]", root);
    const scroller = thumbs.length ? thumbs[0].parentElement : null;

    function scrollThumbIntoStrip(tile) {
      if (!scroller || !tile || !tile.scrollIntoView) return;
      if (scroller.scrollWidth <= scroller.clientWidth + 1) return;
      const view = scroller.getBoundingClientRect();
      const box = tile.getBoundingClientRect();
      if (box.left >= view.left - 1 && box.right <= view.right + 1) return;
      const top = window.scrollY;
      tile.scrollIntoView({ block: "nearest", inline: "nearest", behavior: "instant" });
      if (window.scrollY !== top) window.scrollTo(window.scrollX, top);
    }

    function show(next) {
      if (!total) return;
      const at = ((next % total) + total) % total;
      thumbs.forEach((other, i) => other.classList.toggle("is-active", i === at));
      main.src = thumbs[at].dataset.thumb;
      main.dataset.zoomIndex = thumbs[at].dataset.zoomIndex || String(at + 1);
      if (counter && counter.dataset.galleryCounter) {
        counter.textContent = counter.dataset.galleryCounter.replace("__INDEX__", String(at + 1));
      }
      /* The magnified state belongs to the photo that was on screen, so
         changing photo drops it. `zoom()` owns that state; this is the same
         hand-off `sad:goto` makes in the other direction. */
      main.dispatchEvent(new CustomEvent("sad:unzoom"));
      /* Past the fourth thumb the strip scrolls sideways, and the thumb that is
         now current has to be brought into it. `nearest` on both axes asks for
         the smallest move — but `block: "nearest"` still scrolls the *page*
         when the strip itself is off screen, and a shopper stepping with the
         media box's arrows has not asked the page to move.

         So: it runs only when the tile really is outside the strip's visible
         band, it asks for `instant` (the mock-up's `html { scroll-behavior:
         smooth }` would otherwise make this asynchronous and unrestorable), and
         the page's own position is put back. The restore is in the same task,
         so nothing paints between the two and nothing flickers. */
      scrollThumbIntoStrip(thumbs[at]);
    }

    function current() {
      return Math.max(0, (Number(main.dataset.zoomIndex) || 1) - 1);
    }

    function step(delta) {
      show(current() + delta);
    }

    thumbs.forEach((thumb, i) => thumb.addEventListener("click", () => show(i)));

    /* The viewer telling us where it has got to. */
    main.addEventListener("sad:goto", (event) => show(event.detail.index));

    const prev = $("[data-gallery-prev]", root);
    const next = $("[data-gallery-next]", root);
    if (prev) prev.addEventListener("click", () => step(-1));
    if (next) next.addEventListener("click", () => step(1));

    /* Task 18: the strip's own pair of arrows, at the other end of the row from
       the thumbs. They step the same index with the same wrap, so the big
       photo, the media-box arrows, the thumbs and the viewer stay in step. */
    const stripPrev = $("[data-strip-prev]", root);
    const stripNext = $("[data-strip-next]", root);
    if (stripPrev) stripPrev.addEventListener("click", () => step(-1));
    if (stripNext) stripNext.addEventListener("click", () => step(1));

    /* Left/Right while anything in the media box or the thumbs strip has
       focus. Both boxes are listened to rather than the document, so the arrow
       keys still belong to the page everywhere else. The strip box is the whole
       row (Task 18), so the keys work on the new arrows too. */
    const frame = main.closest(".pdp-media");
    const strip = thumbs.length
      ? thumbs[0].closest(".thumb-row") || thumbs[0].parentElement
      : null;
    [frame, strip].forEach((box) => {
      if (!box || total < 2) return;
      box.addEventListener("keydown", (event) => {
        if (event.key === "ArrowLeft") {
          event.preventDefault();
          step(-1);
        } else if (event.key === "ArrowRight") {
          event.preventDefault();
          step(1);
        }
      });
    });

    /* Horizontal swipe on the lead photo. Pointer Events, so one path covers
       finger, pen and a mouse drag; `pointerup` runs before the click it
       generates, which is what lets the flag suppress it. */
    if (total > 1) {
      let startX = 0;
      let startY = 0;
      let tracking = false;
      main.addEventListener("pointerdown", (event) => {
        tracking = true;
        startX = event.clientX;
        startY = event.clientY;
        delete main.dataset.galleryDragged;
      });
      main.addEventListener("pointerup", (event) => {
        if (!tracking) return;
        tracking = false;
        const dx = event.clientX - startX;
        const dy = event.clientY - startY;
        if (Math.abs(dx) < SWIPE_MIN || Math.abs(dx) <= Math.abs(dy)) return;
        main.dataset.galleryDragged = "1";
        step(dx < 0 ? 1 : -1);
      });
      main.addEventListener("pointercancel", () => {
        tracking = false;
      });
    }
  }

  /* ---------- gallery zoom (Task 14, reshaped in Tasks 17 and 18) -----------
     Client addition: a full-screen viewer for every pointer. The photos are
     1200x1500 and the CDN holds nothing larger, so the viewer paints the
     native file.

     Task 17 removed the hover magnifier the client asked not to have — the lead
     photo no longer moves on its own — and replaced it with a ZOOM chip, an
     explicit control. Tapping the photo still opens the viewer, because that is
     the shopper asking for it.

     Task 18 answered the other half of the same note — "the zoom is not really
     zooming, it just opens the image in a pop-up". A mouse now magnifies the
     photo 2x where it sits and pans it as the pointer moves; a finger still
     opens the viewer. Which one a press gets is decided by the pointer type the
     event reports, falling back to the same media query the CSS is gated on, so
     a hybrid device gets the behaviour that suits the thing being used. On a
     desktop the viewer stays reachable from the keyboard — Enter or Space on
     the focused photo — rather than costing the box another visible control.

     The viewer is a native <dialog> opened with showModal(), so the browser
     owns the focus trap, the inert background and Escape; pinch, pan and
     double-tap are Pointer Events on the scroll-snapped track. Which slide it
     opens at is `data-zoom-index` on the lead photo, which `gallery()` keeps in
     step with the thumbs and the arrows. Without JS the photo is still a
     photo. */
  let closeActiveViewer = null;
  window.addEventListener("popstate", () => {
    if (closeActiveViewer) closeActiveViewer();
  });

  function zoom(root) {
    const lead = $("[data-zoom-lead]", root);
    if (!lead) return;
    if (!bindOnce(root, "zoomBound")) return;

    const clamp = (n, low, high) => Math.min(high, Math.max(low, n));

    const dialog = $("[data-zoom-dialog]", root);
    if (!dialog || typeof dialog.showModal !== "function") return;

    const track = $("[data-zoom-track]", dialog);
    const counter = $("[data-zoom-counter]", dialog);
    const closeButton = $("[data-zoom-close]", dialog);
    const slides = $$("[data-zoom-slide]", dialog);
    const total = slides.length;
    if (!track || !total) return;

    let index = 0;
    let scale = 1;
    let tx = 0;
    let ty = 0;
    let pushedState = false;
    let settling = false;
    let settleTimer = 0;
    let pinchStart = 0;
    let scaleAtPinch = 1;
    let drag = null;
    let lastTap = 0;
    const pointers = new Map();
    /* Task 18, the viewer's mouse path: `mouseZoom` is on while a click (not a
       pinch) magnified it, which is what lets a bare mouse-move pan; `lastMoved`
       is how far the last gesture travelled, so the click a drag produces is not
       read as a zoom; `trackPointer` remembers which pointer type is on the
       track, because `click` is a MouseEvent in Gecko and carries no type. */
    let mouseZoom = false;
    let lastMoved = 0;
    let trackPointer = "";

    const slide = () => slides[index];
    const image = () => (slide() ? $("[data-zoom-image]", slide()) : null);

    function paint() {
      const img = image();
      if (!img) return;
      img.style.transform =
        "translate(" + tx.toFixed(2) + "px, " + ty.toFixed(2) + "px) scale(" + scale.toFixed(4) + ")";
    }

    function setPanning(on) {
      slides.forEach((el) => el.classList.toggle("is-panning", on && el === slide()));
    }

    function resetAll() {
      scale = 1;
      tx = 0;
      ty = 0;
      pinchStart = 0;
      mouseZoom = false;
      slides.forEach((el) => {
        el.classList.remove("is-panning");
        const img = $("[data-zoom-image]", el);
        if (img) img.style.transform = "";
      });
    }

    /* --- gesture bookkeeping ---------------------------------------------
       Every fix below exists because a gesture can end somewhere other than a
       clean pointerup on the track: the dialog can close mid-touch (Escape,
       the back gesture), a finger can drift under the fixed counter/close
       overlays, or one of two fingers can lift. A stale entry in `pointers`
       makes the next single touch read as a pinch against a dead coordinate,
       so the map is cleared on every exit and the pointers are captured to
       the track for the life of the gesture. */
    function releaseCapture(id) {
      try {
        if (track.hasPointerCapture && track.hasPointerCapture(id)) track.releasePointerCapture(id);
      } catch (error) {
        /* the pointer is already gone */
      }
    }

    /* Hand the gesture to the finger that is still down (2 -> 1), so
       "pinch out, lift one finger, drag to look around" keeps panning.
       `moved` starts high so the hand-off can never be read as a tap. */
    function rearmDrag() {
      const remaining = [...pointers.values()][0];
      if (!remaining) {
        drag = null;
        return;
      }
      drag = { x: remaining.x, y: remaining.y, tx: tx, ty: ty, at: Date.now(), moved: 99 };
    }

    /* Drop a pointer the browser took away from us (capture lost, gesture
       cancelled) without running any tap or swipe logic for it. */
    function forgetPointer(event) {
      if (!pointers.has(event.pointerId)) return;
      pointers.delete(event.pointerId);
      if (pointers.size < 2) pinchStart = 0;
      rearmDrag();
    }

    /* Wipe every trace of a gesture — used on open and on close, so a viewer
       that closed with a finger down cannot poison the next one. */
    function resetGesture() {
      pointers.forEach((value, id) => releaseCapture(id));
      pointers.clear();
      drag = null;
      pinchStart = 0;
      lastTap = 0;
    }

    /* Pan is clamped to the photo's own edges: at the limit the image still
       covers the box it filled at 1x, so no empty ground can slide into view. */
    function clampPan() {
      const img = image();
      if (!img) return;
      const limitX = (img.offsetWidth * (scale - 1)) / 2;
      const limitY = (img.offsetHeight * (scale - 1)) / 2;
      tx = clamp(tx, -limitX, limitX);
      ty = clamp(ty, -limitY, limitY);
    }

    /* Scale about a client point, keeping whatever is under it under it. */
    function zoomAt(next, clientX, clientY) {
      const img = image();
      if (!img) return;
      const box = img.getBoundingClientRect();
      const centreX = box.left + box.width / 2 - tx;
      const centreY = box.top + box.height / 2 - ty;
      const u = (clientX - centreX - tx) / scale;
      const v = (clientY - centreY - ty) / scale;
      scale = clamp(next, 1, 4);
      if (scale === 1) {
        tx = 0;
        ty = 0;
      } else {
        tx = clientX - centreX - scale * u;
        ty = clientY - centreY - scale * v;
        clampPan();
      }
      setPanning(scale > 1);
      paint();
    }

    /* Task 18: pan by pointing rather than by dragging. The cursor's position
       across the photo maps to the same position across the magnified area, so
       the far left of the frame shows the far left of the picture — the same
       mapping `transform-origin` gives the media box's in-place zoom. */
    function panToPointer(clientX, clientY) {
      const img = image();
      if (!img || scale <= 1) return;
      const box = img.getBoundingClientRect();
      const halfW = img.offsetWidth / 2;
      const halfH = img.offsetHeight / 2;
      if (!halfW || !halfH) return;
      const centreX = box.left + box.width / 2 - tx;
      const centreY = box.top + box.height / 2 - ty;
      const u = clamp((clientX - centreX) / halfW, -1, 1);
      const v = clamp((clientY - centreY) / halfH, -1, 1);
      tx = -u * halfW * (scale - 1);
      ty = -v * halfH * (scale - 1);
      clampPan();
      paint();
    }

    function updateCounter() {
      if (!counter || total < 2) return;
      const template = counter.dataset.zoomCounter;
      if (template) counter.textContent = template.replace("__INDEX__", String(index + 1));
    }

    /* Hand the index back to `gallery()`, which owns it. Everything the shopper
       sees behind the viewer — the lead photo, the thumbs' active ring, where
       the arrows step from and where the ZOOM chip reopens — follows from
       there, so closing the viewer on photo 3 leaves the page on photo 3. */
    function announce() {
      lead.dispatchEvent(new CustomEvent("sad:goto", { detail: { index: index } }));
    }

    function goTo(next, smooth) {
      index = clamp(next, 0, total - 1);
      resetAll();
      updateCounter();
      announce();
      settling = true;
      clearTimeout(settleTimer);
      settleTimer = setTimeout(() => {
        settling = false;
      }, smooth ? 480 : 0);
      track.scrollTo({ left: index * track.clientWidth, behavior: smooth ? "smooth" : "instant" });
    }

    /* The viewer's arrows and its Left/Right keys wrap, exactly as the media
       box's do. A wrap is a jump across the whole track, so it lands instantly
       rather than scanning every slide on the way past. */
    function step(delta) {
      const next = ((index + delta) % total + total) % total;
      goTo(next, Math.abs(next - index) === 1);
    }

    function openViewer(at) {
      if (dialog.open) return;
      /* the media box hands its magnification over to the viewer, so closing
         the viewer does not reveal a photo still stuck at 2x behind it */
      magnify(false);
      resetGesture();
      dialog.classList.remove("is-touched");
      dialog.showModal();
      document.body.classList.add("zoom-open");
      goTo((Number(at) || 1) - 1, false);
      try {
        history.pushState({ sadZoom: true }, "");
        pushedState = true;
      } catch (error) {
        /* history is unavailable (sandboxed frame) — the button still closes */
      }
      closeActiveViewer = () => {
        if (!dialog.open) return;
        pushedState = false;
        dialog.close();
      };
      if (closeButton) closeButton.focus();
    }

    /* --- Task 18: magnify in place, on a click ----------------------------
       The state is one class on the frame plus an inline `transform-origin`;
       the scale, the clipping and the cursors are CSS, inside the same
       `(hover: hover) and (pointer: fine)` query this code matches. That is
       deliberate: if the query is false the class paints nothing, so the worst
       a mistake here can do is nothing at all.

       It is a click and never a hover — the client's standing rule since Task
       17 — and it resets on a second click, on the chip, on Escape and on any
       change of photo. */
    const frame = lead.closest(".pdp-media");
    const chip = $("[data-zoom-open]", root);
    const chipLabel = chip ? $("[data-zoom-label]", chip) : null;
    const labelIn = chipLabel ? chipLabel.textContent : "";
    const labelOut = chip && chip.dataset.zoomLabelOut ? chip.dataset.zoomLabelOut : labelIn;
    const finePointer = () =>
      typeof window.matchMedia === "function" &&
      window.matchMedia("(hover: hover) and (pointer: fine)").matches;

    let magnified = false;
    /* `click` is a PointerEvent in Chromium and WebKit but a MouseEvent in
       Gecko, so the last pointerdown anywhere in the box is remembered as the
       fallback. Capture phase: the chip is a child and stops nothing. */
    let boxPointer = "";
    if (frame) {
      frame.addEventListener(
        "pointerdown",
        (event) => {
          boxPointer = event.pointerType || "";
        },
        true
      );
    }

    /* A press that should magnify rather than open the viewer: a mouse or a pen
       on a screen whose CSS gate is true. A finger always gets the viewer. */
    function magnifies(event) {
      if (!frame) return false;
      const kind = event.pointerType || boxPointer;
      if (kind === "touch") return false;
      return finePointer();
    }

    function aimAt(clientX, clientY) {
      if (!frame) return;
      const box = frame.getBoundingClientRect();
      if (!box.width || !box.height) return;
      const x = clamp(((clientX - box.left) / box.width) * 100, 0, 100);
      const y = clamp(((clientY - box.top) / box.height) * 100, 0, 100);
      lead.style.transformOrigin = x.toFixed(2) + "% " + y.toFixed(2) + "%";
    }

    function magnify(on, clientX, clientY) {
      if (!frame) return;
      magnified = !!on;
      if (chip) chip.setAttribute("aria-pressed", String(magnified));
      frame.classList.toggle("is-zoomed", magnified);
      if (magnified) aimAt(clientX, clientY);
      else lead.style.transformOrigin = "";
      if (chipLabel) chipLabel.textContent = magnified ? labelOut : labelIn;
    }

    /* While it is magnified the pointer chooses what is under the glass. The
       listener is on the frame, so the arrows and the chip keep the aim steady
       under themselves rather than snapping it to the corner. */
    if (frame) {
      frame.addEventListener("mousemove", (event) => {
        if (magnified) aimAt(event.clientX, event.clientY);
      });
    }

    /* `gallery()` fires this from `show()` — arrows, thumbs, keys, the viewer's
       own moves. One index, one owner. */
    lead.addEventListener("sad:unzoom", () => {
      if (magnified) magnify(false);
    });

    document.addEventListener("keydown", (event) => {
      if (event.key === "Escape" && magnified) magnify(false);
    });

    /* A swipe on the lead photo produces a click too; `gallery()` flags the one
       that has just stepped the gallery so it cannot also magnify or open the
       viewer. */
    lead.addEventListener("click", (event) => {
      if (lead.dataset.galleryDragged === "1") {
        delete lead.dataset.galleryDragged;
        return;
      }
      if (magnifies(event)) {
        magnify(!magnified, event.clientX, event.clientY);
        return;
      }
      openViewer(lead.dataset.zoomIndex);
    });
    lead.addEventListener("keydown", (event) => {
      if (event.key !== "Enter" && event.key !== " " && event.key !== "Spacebar") return;
      event.preventDefault();
      openViewer(lead.dataset.zoomIndex);
    });

    /* Part B (Task 17): the explicit control. A real <button>, so it is in the
       tab order and answers Enter and Space without any help from here.

       Task 18 gave it the second half of the job: on a fine pointer it is the
       in-place magnifier's on/off switch and says so ("Zoom out" while the
       photo is magnified). There is no pointer position on the photo to aim at
       when the press landed on the chip, so it magnifies about the centre. */
    if (chip) {
      chip.addEventListener("click", (event) => {
        if (magnifies(event)) {
          const box = frame.getBoundingClientRect();
          magnify(!magnified, box.left + box.width / 2, box.top + box.height / 2);
          return;
        }
        openViewer(lead.dataset.zoomIndex);
      });
    }

    /* Part C: the viewer's own arrows. */
    const viewerPrev = $("[data-zoom-prev]", dialog);
    const viewerNext = $("[data-zoom-next]", dialog);
    if (viewerPrev) viewerPrev.addEventListener("click", () => step(-1));
    if (viewerNext) viewerNext.addEventListener("click", () => step(1));

    if (closeButton) closeButton.addEventListener("click", () => dialog.close());

    dialog.addEventListener("close", () => {
      document.body.classList.remove("zoom-open");
      resetGesture();
      resetAll();
      closeActiveViewer = null;
      lead.focus();
      if (!pushedState) return;
      pushedState = false;
      try {
        history.back();
      } catch (error) {
        /* nothing to unwind */
      }
    });

    dialog.addEventListener("keydown", (event) => {
      if (total < 2) return;
      if (event.key === "ArrowRight") {
        event.preventDefault();
        step(1);
      } else if (event.key === "ArrowLeft") {
        event.preventDefault();
        step(-1);
      }
    });

    track.addEventListener("scroll", () => {
      if (settling || !track.clientWidth) return;
      const next = clamp(Math.round(track.scrollLeft / track.clientWidth), 0, total - 1);
      if (next === index) return;
      index = next;
      resetAll();
      updateCounter();
      announce();
    });

    /* --- pinch / pan / double-tap -------------------------------------- */
    track.addEventListener("pointerdown", (event) => {
      dialog.classList.add("is-touched");
      trackPointer = event.pointerType || "";
      /* Capture keeps the move/up events coming even when the finger drifts
         over the fixed counter and close button, which are siblings of the
         track and would otherwise swallow them. */
      try {
        track.setPointerCapture(event.pointerId);
      } catch (error) {
        /* capture is unavailable — the gesture still works inside the track */
      }
      pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
      if (pointers.size === 2) {
        const two = [...pointers.values()];
        pinchStart = Math.hypot(two[0].x - two[1].x, two[0].y - two[1].y);
        scaleAtPinch = scale;
        drag = null;
      } else if (pointers.size === 1) {
        drag = { x: event.clientX, y: event.clientY, tx: tx, ty: ty, at: Date.now(), moved: 0 };
      }
    });

    track.addEventListener("pointermove", (event) => {
      if (!pointers.has(event.pointerId)) return;
      pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });

      if (pointers.size >= 2) {
        const two = [...pointers.values()];
        const spread = Math.hypot(two[0].x - two[1].x, two[0].y - two[1].y);
        if (pinchStart > 0) {
          zoomAt(scaleAtPinch * (spread / pinchStart), (two[0].x + two[1].x) / 2, (two[0].y + two[1].y) / 2);
        }
        event.preventDefault();
        return;
      }

      if (!drag) return;
      const dx = event.clientX - drag.x;
      const dy = event.clientY - drag.y;
      drag.moved = Math.max(drag.moved, Math.hypot(dx, dy));
      /* At 1x the browser owns the horizontal gesture (scroll-snap paging);
         above 1x the slide is `touch-action: none` and the drag pans. */
      if (scale > 1) {
        tx = drag.tx + dx;
        ty = drag.ty + dy;
        clampPan();
        paint();
        event.preventDefault();
      }
    });

    function releasePointer(event) {
      const wasSingle = pointers.size === 1;
      const start = drag;
      /* how far this gesture travelled, read by the click handler below — the
         click a drag produces must not also toggle the magnification */
      lastMoved = start ? start.moved : 0;
      pointers.delete(event.pointerId);
      releaseCapture(event.pointerId);
      if (pointers.size < 2) pinchStart = 0;
      if (scale <= 1.02) {
        resetAll();
        paint();
      }
      if (pointers.size > 0) {
        rearmDrag();
        return;
      }
      drag = null;
      if (!wasSingle || !start || event.pointerType === "mouse") return;

      const now = Date.now();
      if (start.moved < 14 && now - start.at < 320) {
        if (now - lastTap < 320) {
          lastTap = 0;
          zoomAt(scale > 1 ? 1 : 2.5, event.clientX, event.clientY);
        } else {
          lastTap = now;
        }
        return;
      }
      /* swipe down closes, but only while the photo is not zoomed in */
      const dy = event.clientY - start.y;
      const dx = event.clientX - start.x;
      if (scale === 1 && dy > 80 && Math.abs(dx) < 60) dialog.close();
    }

    track.addEventListener("pointerup", releasePointer);
    track.addEventListener("pointercancel", forgetPointer);
    track.addEventListener("lostpointercapture", forgetPointer);

    /* --- Task 18: the viewer's mouse path ---------------------------------
       The finger has had a double-tap since Task 14; the mouse had a double
       click, which is a poor fit — the client's note was that the pop-up did
       not zoom, and a control nobody finds is the same as no control. A single
       click now toggles the same 2.5x about the cursor, and moving the mouse
       pans it, so a mouse never has to drag.

       The old `dblclick` handler is gone rather than kept alongside: a double
       click is two clicks, so it would zoom in and straight back out. Touch is
       untouched — its double-tap lives in `releasePointer()` above. */
    track.addEventListener("click", (event) => {
      const kind = event.pointerType || trackPointer;
      if (kind !== "mouse") return;
      if (lastMoved > 6) return;
      if (scale > 1) {
        zoomAt(1, event.clientX, event.clientY);
        mouseZoom = false;
        return;
      }
      zoomAt(2.5, event.clientX, event.clientY);
      mouseZoom = scale > 1;
    });

    track.addEventListener("mousemove", (event) => {
      if (!mouseZoom || scale <= 1 || pointers.size) return;
      panToPointer(event.clientX, event.clientY);
    });
  }

  /* ---------- variant picker ---------- */
  function variantPicker(root) {
    const json = $("[data-product-json]", root);
    if (!json) return;

    const data = JSON.parse(json.textContent);
    const idInput = $('input[name="id"]', root);
    const price = $("[data-price]", root);
    const was = $("[data-price-was]", root);
    const saleBadge = $("[data-sale-badge]", root);
    const addButton = $("[data-add]", root);
    const buttons = $$(".size", root);

    buttons.forEach((button) => {
      button.addEventListener("click", () => {
        if (button.disabled) return;

        const variant = data.variants.find((v) => String(v.id) === button.dataset.variantId);
        if (!variant) return;

        buttons.forEach((other) => {
          other.classList.remove("is-active");
          other.setAttribute("aria-pressed", "false");
        });
        button.classList.add("is-active");
        button.setAttribute("aria-pressed", "true");

        /* Task 19: the measurements block marks the row for the chosen size.
           It is a module of its own with its own view of the table, so the size
           travels as an event rather than as a second copy of "which variant" —
           the same hand-off `sad:goto` makes between the gallery and the
           viewer. The size is the one the server put on the button, not the
           button's text, which also carries the "Sold out" caption. */
        root.dispatchEvent(
          new CustomEvent("sad:size", {
            detail: { size: button.dataset.size || variant.option1 || "" },
          })
        );

        const reduced = variant.compare_at_price > variant.price;

        if (idInput) idInput.value = variant.id;
        if (price) price.textContent = variant.price_money || money(variant.price);
        if (was) {
          was.hidden = !reduced;
          was.textContent = reduced
            ? variant.compare_at_price_money || money(variant.compare_at_price)
            : "";
        }
        /* Sale is the one per-variant chip (spec 5.6); the rest are product-level
           and stay as the server rendered them. */
        if (saleBadge) saleBadge.hidden = !reduced;
        if (addButton) addButton.disabled = !variant.available;

        const url = new URL(window.location.href);
        url.searchParams.set("variant", variant.id);
        window.history.replaceState({}, "", url);
      });
    });
  }

  /* ---------- measurements (Task 19) ---------------------------------------
     Client feedback: "the measurements on each product listing should be more
     obvious". The table is the client's own HTML inside `product.description`
     — no classes, no ids, rows labelled with the size the shopper reads on the
     buttons above — so everything here works off that one anchor: the label in
     each row's first cell.

     Three jobs, none of which may throw on a description that has no table (a
     few one-off pieces carry only the prose):

       * mute the rows of sold-out sizes. Their availability comes from the
         variants, rendered onto the block as JSON, because the table itself
         says nothing about stock and the client asked for the whole size run to
         stay visible.
       * mark the chosen size's row, on load from the size button the server
         rendered active, and after that from `variantPicker()`'s `sad:size`.
       * open the how-we-measure sheet in a modal <dialog>: the browser owns the
         focus trap and Escape, and the only thing left to do here is put focus
         back on the chip.

     Labels are compared trimmed, whitespace-collapsed and case-insensitively,
     which is what lets "XS (Womens)" and "XS" — two rows and two variants on
     the same product — keep to their own rows. */
  function measurements(root) {
    const block = $("[data-measure]", root);
    if (!block) return;
    if (!bindOnce(block, "measureBound")) return;

    const table = $(".pdp-measure__body table", block);
    /* Header rows are the ones made of <th>; everything else is a size. */
    const rows = table
      ? $$("tr", table).filter((row) => row.cells.length && row.cells[0].tagName !== "TH")
      : [];

    const label = (text) =>
      String(text == null ? "" : text).replace(/\s+/g, " ").trim().toLowerCase();

    function rowFor(size) {
      const wanted = label(size);
      if (!wanted) return null;
      return rows.find((row) => label(row.cells[0].textContent) === wanted) || null;
    }

    let availability = {};
    try {
      availability = JSON.parse(block.dataset.sizeAvailability || "{}") || {};
    } catch (error) {
      /* a hand-edited description — the table still renders, unmuted */
    }
    Object.keys(availability).forEach((size) => {
      if (availability[size]) return;
      const row = rowFor(size);
      if (row) row.classList.add("is-soldout");
    });

    /* `aria-current` alongside the class: the left rule and the ink are a
       sighted cue, and a screen reader has nothing to read from a class. It is
       set on the row rather than a cell so the announcement lands once, with
       the whole row, and it is removed rather than set to "false" — an absent
       `aria-current` is the only way to say "not the current one". */
    function highlight(size) {
      const row = rowFor(size);
      rows.forEach((other) => {
        const current = other === row;
        other.classList.toggle("is-current", current);
        if (current) other.setAttribute("aria-current", "true");
        else other.removeAttribute("aria-current");
      });
    }

    const active = $(".size.is-active", root);
    highlight(active ? active.dataset.size : "");
    root.addEventListener("sad:size", (event) => highlight(event.detail.size));

    const chip = $("[data-measure-open]", block);
    const dialog = $("[data-measure-dialog]", root);
    if (!chip || !dialog || typeof dialog.showModal !== "function") return;

    chip.addEventListener("click", () => dialog.showModal());
    $$("[data-measure-close]", dialog).forEach((el) =>
      el.addEventListener("click", () => dialog.close())
    );
    /* A press on the backdrop lands on the <dialog> element itself; anything
       inside it targets a child, so this closes on outside clicks only. */
    dialog.addEventListener("click", (event) => {
      if (event.target === dialog) dialog.close();
    });
    /* Escape and the close button both end here, which is the whole of the
       focus management: the browser has already restored the background. */
    dialog.addEventListener("close", () => chip.focus());
  }

  /* ---------- cart ---------- */
  /* `fetch` rejects (rather than resolving with !ok) when the network drops or
     the request is blocked. Without this the button would just re-enable through
     `finally` and the shopper would be told nothing, plus an unhandled rejection
     in the console. */
  async function post(url, options) {
    try {
      return await fetch(url, options);
    } catch (error) {
      return null;
    }
  }

  async function addToCart(form) {
    const addButton = $("[data-add]", form);
    if (addButton) addButton.disabled = true;
    try {
      const response = await post(routes().add + ".js", {
        method: "POST",
        body: new FormData(form),
        headers: { Accept: "application/json" },
      });
      if (!response) {
        window.alert(strings().addError);
        return;
      }
      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        window.alert(error.description || error.message || strings().addError);
        return;
      }
      await refreshCart();
      openDrawer();
    } finally {
      if (addButton) addButton.disabled = false;
    }
  }

  /* Shopify answers a rejected quantity (over stock, gone) with 4xx and a
     `description`. Report it, then refresh either way so the input snaps back to
     the quantity the cart actually holds. */
  async function changeLine(key, quantity) {
    const response = await post(routes().change + ".js", {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ id: key, quantity: quantity }),
    });
    if (!response) {
      window.alert(strings().changeError);
      return false;
    }
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      window.alert(error.description || error.message || strings().changeError);
    }
    await refreshCart();
    return response.ok;
  }

  async function refreshCart() {
    const cartResponse = await post(routes().cart + ".js", {
      headers: { Accept: "application/json" },
    });
    if (!cartResponse || !cartResponse.ok) return null;
    const cart = await cartResponse.json().catch(() => null);
    if (!cart) return null;

    const label = strings().cartPill.replace("__COUNT__", cart.item_count);
    $$("[data-cart-count]").forEach((el) => {
      el.textContent = label;
      el.setAttribute("aria-label", label);
    });

    /* Re-render the drawer in the context of the page we are on, so market and
       locale URL prefixes are kept. No Accept header here: with
       `Accept: application/json` a product or cart URL answers with its own
       resource JSON instead of the requested section. */
    const sectionUrl = new URL(window.location.href);
    sectionUrl.search = "";
    sectionUrl.searchParams.set("sections", "cart-drawer");
    const sectionResponse = await post(sectionUrl);
    if (!sectionResponse || !sectionResponse.ok) return cart;
    const sections = await sectionResponse.json().catch(() => null);
    if (!sections) return cart;

    const current = $("#cart-drawer");
    if (current && sections["cart-drawer"]) {
      const holder = document.createElement("div");
      holder.innerHTML = sections["cart-drawer"];
      const next = $("#cart-drawer", holder);
      if (next) {
        if (current.hasAttribute("open")) next.setAttribute("open", "");
        current.replaceWith(next);
        bindDrawer();
      }
    }
    return cart;
  }

  /* ---------- drawer ---------- */
  let drawerOpener = null;

  function openDrawer() {
    const drawer = $("#cart-drawer");
    if (!drawer) return;
    if (!drawer.hasAttribute("open")) drawerOpener = document.activeElement;
    drawer.setAttribute("open", "");
    document.body.classList.add("drawer-open");
    const close = $(".cart-drawer__close", drawer);
    if (close) close.focus();
  }

  function closeDrawer() {
    const drawer = $("#cart-drawer");
    if (!drawer || !drawer.hasAttribute("open")) return;
    drawer.removeAttribute("open");
    document.body.classList.remove("drawer-open");
    if (drawerOpener && document.contains(drawerOpener)) drawerOpener.focus();
    drawerOpener = null;
  }

  function bindDrawer() {
    const drawer = $("#cart-drawer");
    if (!drawer) return;

    $$("[data-drawer-close]", drawer).forEach((el) => el.addEventListener("click", closeDrawer));

    $$("[data-line-change]", drawer).forEach((input) => {
      input.addEventListener("change", () => {
        changeLine(input.dataset.lineChange, parseInt(input.value, 10) || 0);
      });
    });

    $$("[data-line-remove]", drawer).forEach((link) => {
      link.addEventListener("click", (event) => {
        event.preventDefault();
        changeLine(link.dataset.lineRemove, 0);
      });
    });
  }

  /* ---------- cart page ----------
     The cart page uses the same line item as the drawer, so it uses the same
     Cart API call. `changeLine` already refreshes the pill and the drawer; the
     page itself is re-rendered through the Section Rendering API, which is what
     `data-cart-page` carries the section id for. */
  async function refreshCartPage() {
    const page = $("[data-cart-page]");
    if (!page) return;
    const id = page.dataset.cartPage;

    const url = new URL(window.location.href);
    url.search = "";
    url.searchParams.set("sections", id);
    const response = await post(url);
    if (!response || !response.ok) return;
    const sections = await response.json().catch(() => null);
    if (!sections || !sections[id]) return;

    const holder = document.createElement("div");
    holder.innerHTML = sections[id];
    const next = $("[data-cart-page]", holder);
    if (!next) return;
    page.replaceWith(next);
    bindCartPage();
  }

  function bindCartPage() {
    const page = $("[data-cart-page]");
    if (!page) return;

    $$("[data-line-change]", page).forEach((input) => {
      input.addEventListener("change", async () => {
        await changeLine(input.dataset.lineChange, parseInt(input.value, 10) || 0);
        await refreshCartPage();
      });
    });

    $$("[data-line-remove]", page).forEach((link) => {
      link.addEventListener("click", async (event) => {
        event.preventDefault();
        await changeLine(link.dataset.lineRemove, 0);
        await refreshCartPage();
      });
    });
  }

  /* ---------- recommendations ---------- */
  async function loadRecommendations(el) {
    const url = el.dataset.recommendations;
    if (!url) return;
    try {
      const response = await fetch(url);
      if (!response.ok) return;
      const holder = document.createElement("div");
      holder.innerHTML = await response.text();
      const fresh = $("[data-recommendations]", holder);
      if (fresh) el.innerHTML = fresh.innerHTML;
      /* These cards arrive long after init() has run, and on a product page
         they are the only cards on it — so this is where the card photos get
         their one-off nudge offered to them. The bars and the hover scrub need
         nothing here: they are delegated from `document`. */
      cardPhotos(el);
    } catch (error) {
      /* no recommendations yet — the band stays empty */
    }
  }

  /* ---------- ask-us panel (Task 16) ----------
     The floating button is a plain link to Telegram in the markup, so with JS
     off it is the link it has always been. Here it becomes the trigger for a
     small non-modal dialog carrying both message-us channels and the enquiry
     line: the page behind it stays scrollable and usable, which is why it is a
     `role="dialog"` box rather than `<dialog>.showModal()`.

     There is exactly one panel per page — the layout renders the section once —
     so the parts are looked up from the document at event time. That is what
     lets the theme editor swap the section out from under the two global
     listeners without leaving them pointing at a detached node. */
  let askUsGlobals = false;

  function askUsParts() {
    const shell = $("[data-ask-us]");
    if (!shell) return null;
    const toggle = $("[data-ask-toggle]", shell);
    const panel = $("[data-ask-panel]", shell);
    return toggle && panel ? { shell, toggle, panel } : null;
  }

  function openAskUs() {
    const parts = askUsParts();
    if (!parts) return;
    parts.panel.hidden = false;
    parts.toggle.setAttribute("aria-expanded", "true");
    const close = $("[data-ask-close]", parts.panel);
    if (close) close.focus();
  }

  /* `restoreFocus` is false for a click-away: the shopper is already reaching
     for something else, and pulling focus back to the button would scroll the
     page to it. Escape and the close button do return it. */
  function closeAskUs(restoreFocus) {
    const parts = askUsParts();
    if (!parts || parts.panel.hidden) return;
    parts.panel.hidden = true;
    parts.toggle.setAttribute("aria-expanded", "false");
    if (restoreFocus !== false) parts.toggle.focus();
  }

  function askUs(root) {
    const shell = $("[data-ask-us]", root || document);
    if (!shell || shell.dataset.askBound === "1") return;
    const parts = askUsParts();
    if (!parts) return;
    shell.dataset.askBound = "1";

    /* Progressive enhancement: the ARIA wiring is added here, not in the
       Liquid, so a button that never gets JS never claims to control a panel. */
    parts.panel.hidden = true;
    parts.toggle.setAttribute("aria-haspopup", "dialog");
    parts.toggle.setAttribute("aria-controls", parts.panel.id);
    parts.toggle.setAttribute("aria-expanded", "false");

    parts.toggle.addEventListener("click", (event) => {
      event.preventDefault();
      if (parts.panel.hidden) openAskUs();
      else closeAskUs();
    });

    $$("[data-ask-close]", parts.panel).forEach((el) => {
      el.addEventListener("click", (event) => {
        event.preventDefault();
        closeAskUs();
      });
    });

    if (askUsGlobals) return;
    askUsGlobals = true;

    document.addEventListener("keydown", (event) => {
      if (event.key === "Escape") closeAskUs();
    });

    document.addEventListener("click", (event) => {
      const open = askUsParts();
      if (!open || open.panel.hidden || open.shell.contains(event.target)) return;
      closeAskUs(false);
    });
  }

  /* ---------- the floating button vs Add to cart (Task 17) ------------------
     The floating "Ask us" button sits bottom-right, where the product page's
     Add to cart passes under it for a band of scroll positions. While the
     button the shopper came for is on screen, the floating one gets out of its
     way.

     An IntersectionObserver on the Add-to-cart button rather than a scroll
     listener: it is the button's own geometry that decides, so a longer product
     column, a taller phone or a change to either page costs nothing here.

     Task 19 fix round 1: at every width, not only on a phone. The measurements
     table put 300px of figures directly above the button, and at 1440 the pill
     covered the last row of a six-size table — which the shopper is reading at
     exactly the moment the button is on screen. Nothing else changes: a page
     with no `[data-add]` never gets an observer, so the class never appears on
     home, Shop or the cart. */
  function fabDodge(root) {
    const shell = $("[data-ask-us]");
    const add = $("[data-add]", root || document);
    if (!shell || !add || typeof IntersectionObserver !== "function") return;
    if (add.dataset.fabWatched === "1") return;
    add.dataset.fabWatched = "1";
    /* The bottom margin makes the button count as "arriving" 150px before it
       enters the window. At 1440x900 Add to cart sits ~20px below the fold on
       an unscrolled six-size product page, and without the margin the pill
       still covered the last table row until the shopper scrolled 24px. */
    new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        shell.classList.toggle("is-cta-visible", entry.isIntersecting);
      });
    }, { rootMargin: "0px 0px 150px 0px" }).observe(add);
  }

  /* ---------- reviews carousel (Task 21) ------------------------------------
     The home reviews, three a view on a desktop, two on a tablet, one on a
     phone. The scrolling itself is the browser's: the row is a scroll-snapping
     track, so a swipe, a trackpad and the arrow keys all work with none of this
     running. What this adds is the two arrows, the dots, and the auto-advance.

     Nothing here decides how many cards fit — CSS does, and this measures the
     answer off the track (`clientWidth` against one card's pitch). That is the
     whole reason the breakpoints are not repeated in JS: the widths are set in
     one place, and a change to them cannot leave the dots counting pages that
     do not exist.

     The auto-advance stops rather than pauses whenever someone might be reading
     — a hover, a focus inside, a finger on the track, a hidden tab — and never
     starts at all under `prefers-reduced-motion: reduce`. The holds are a set
     rather than a flag because they overlap: a shopper who tabs into a card
     they were already hovering must not have it start moving again the moment
     the pointer leaves. */

  /* One MediaQueryList and one registry for every carousel on the page, so the
     three listeners that are not per-element — the tab being hidden, the window
     being resized, the motion preference changing — are bound once for all of
     them instead of once per bind. The theme editor re-renders a section
     without reloading the page, so "once per bind" would pile up a set of
     document- and window-level listeners for the rest of the visit; each stale
     one would be a no-op, but its stale interval could still fire. */
  const reviewsStill = window.matchMedia("(prefers-reduced-motion: reduce)");
  const reviewsLive = new Set();
  let reviewsGlobals = false;

  /* Drop a carousel and silence it in the same breath — the timer goes now, not
     on its next tick. */
  function reviewsDrop(car) {
    reviewsLive.delete(car);
    car.stop();
  }

  /* Drop every carousel whose section has been taken out from under us. A
     `shopify:section:unload` below catches removals as they happen; this is the
     belt to that brace and covers a section replaced without one — measured:
     re-rendering the section through the Section Rendering API and firing only
     `shopify:section:load` leaves the old carousel registered, and without this
     it stays there until the next resize. It runs on every global event and on
     every bind, so a re-render always clears its predecessor. */
  function reviewsSweep() {
    [...reviewsLive].forEach((car) => {
      if (!car.root.isConnected) reviewsDrop(car);
    });
  }

  function reviewsEach(fn) {
    reviewsSweep();
    reviewsLive.forEach(fn);
  }

  function reviewsGlobalsOnce() {
    if (reviewsGlobals) return;
    reviewsGlobals = true;

    document.addEventListener("visibilitychange", () => {
      reviewsEach((car) => (document.hidden ? car.stop() : car.start()));
    });

    let resizing = 0;
    window.addEventListener("resize", () => {
      if (resizing) cancelAnimationFrame(resizing);
      resizing = requestAnimationFrame(() => {
        resizing = 0;
        reviewsEach((car) => car.relayout());
      });
    });

    const onMotion = () => reviewsEach((car) => {
      car.stop();
      car.start();
    });
    if (reviewsStill.addEventListener) reviewsStill.addEventListener("change", onMotion);
    else if (reviewsStill.addListener) reviewsStill.addListener(onMotion);

    /* Editor only: the section is going, so its timer goes with it — at the
       moment of removal rather than whenever something next looks. The event
       fires while the section is still in the tree, which is why this matches
       on the section rather than on `isConnected`. */
    document.addEventListener("shopify:section:unload", (event) => {
      if (!event.target || !event.target.contains) return;
      [...reviewsLive].forEach((car) => {
        if (event.target.contains(car.root)) reviewsDrop(car);
      });
    });
  }

  function reviewsCarousel(root) {
    const track = $("[data-reviews-track]", root);
    if (!track) return;
    const cards = $$(".review", track);
    /* One review is not a carousel: no arrows, no dots, nothing moving. The
       section renders no controls in that case either. */
    if (cards.length < 2) return;
    if (!bindOnce(root, "reviewsBound")) return;

    const dotsBox = $("[data-reviews-dots]", root);
    const prev = $("[data-reviews-prev]", root);
    const next = $("[data-reviews-next]", root);
    const still = reviewsStill;
    /* Absent means on: the setting's own default, so a section saved before
       Task 21 behaves like one saved after it. */
    const wanted = root.dataset.reviewsAutoplay !== "false";
    const seconds = Math.min(12, Math.max(3, Number(root.dataset.reviewsSeconds) || 6));

    let step = 0; // one card plus the mock-up's 18px gap
    let perView = 1;
    let pages = 1;
    let timer = null;
    const holds = new Set();

    function measure() {
      const first = cards[0].getBoundingClientRect();
      step = cards[1].getBoundingClientRect().left - first.left;
      if (step <= 0) step = first.width;
      perView = step > 0 ? Math.max(1, Math.round(track.clientWidth / step)) : 1;
      pages = Math.ceil(cards.length / perView);
      /* Two or three reviews are one page at a desktop width, and an arrow with
         nowhere to go is worse than no arrow. Re-read on every resize, because
         the same section is three pages on a phone. */
      root.classList.toggle("is-single", pages < 2);
    }

    function maxScroll() {
      return track.scrollWidth - track.clientWidth;
    }

    /* Which page is on screen, read from the scroll position rather than kept
       as a second copy of it — a swipe moves the track without asking. The end
       of the track is the last page even when it is a short one (two a view
       over nine cards leaves the ninth alone), which is where a page index
       computed from the pitch alone would come up one short. */
    function pageAt() {
      const max = maxScroll();
      if (max <= 1 || !step) return 0;
      if (track.scrollLeft >= max - 2) return pages - 1;
      return Math.max(0, Math.min(pages - 1, Math.round(track.scrollLeft / (perView * step))));
    }

    function mark(at) {
      if (!dotsBox) return;
      $$(".reviews-dot", dotsBox).forEach((dot, i) => {
        if (i === at) dot.setAttribute("aria-current", "true");
        else dot.removeAttribute("aria-current");
      });
    }

    function goTo(page, behavior) {
      const at = ((page % pages) + pages) % pages; // last -> first, first -> last
      const left = Math.min(at * perView * step, maxScroll());
      /* `instant` and not `auto`: the track's CSS asks for smooth scrolling, so
         `auto` would resolve back to it and a device asking for less motion
         would still get the glide. */
      const how = behavior || (still.matches ? "instant" : "smooth");
      if (track.scrollTo) track.scrollTo({ left: left, behavior: how });
      else track.scrollLeft = left;
      mark(at);
    }

    /* One dot per page, labelled from the template the section rendered — the
       count is a measurement, so the string cannot be built in Liquid. */
    function buildDots() {
      if (!dotsBox) return;
      const template = dotsBox.dataset.pageLabel || "";
      dotsBox.textContent = "";
      for (let i = 0; i < pages; i += 1) {
        const dot = document.createElement("button");
        dot.type = "button";
        dot.className = "reviews-dot";
        dot.setAttribute(
          "aria-label",
          template.replace("__INDEX__", String(i + 1)).replace("__TOTAL__", String(pages))
        );
        dot.addEventListener("click", () => {
          goTo(i);
          start();
        });
        dotsBox.appendChild(dot);
      }
      mark(pageAt());
    }

    function stop() {
      if (!timer) return;
      clearInterval(timer);
      timer = null;
    }

    function start() {
      stop();
      if (!wanted || still.matches || pages < 2 || holds.size || document.hidden) return;
      timer = setInterval(() => {
        /* The theme editor removes a section without telling its timers. */
        if (!root.isConnected) {
          stop();
          return;
        }
        goTo(pageAt() + 1);
      }, seconds * 1000);
    }

    function hold(why) {
      holds.add(why);
      stop();
    }

    function release(why) {
      holds.delete(why);
      start();
    }

    /* A hover hold, and only from something that can actually hover. Measured
       on an iPhone 13: a tap inside the carousel makes WebKit synthesise the
       whole mouse sequence, so `mouseenter` fires with no `mouseleave` ever to
       follow it — one tap and the carousel would stop for the rest of the
       visit. A finger's pause is the touch hold below, which does end. */
    root.addEventListener("pointerenter", (event) => {
      if (event.pointerType === "touch") return;
      hold("hover");
    });
    root.addEventListener("pointerleave", (event) => {
      if (event.pointerType === "touch") return;
      release("hover");
    });
    root.addEventListener("focusin", () => hold("focus"));
    root.addEventListener("focusout", (event) => {
      if (!root.contains(event.relatedTarget)) release("focus");
    });
    track.addEventListener("touchstart", () => hold("touch"), { passive: true });
    track.addEventListener("touchend", () => release("touch"), { passive: true });
    track.addEventListener("touchcancel", () => release("touch"), { passive: true });

    if (prev) {
      prev.addEventListener("click", () => {
        goTo(pageAt() - 1);
        start();
      });
    }
    if (next) {
      next.addEventListener("click", () => {
        goTo(pageAt() + 1);
        start();
      });
    }

    /* Left/Right anywhere in the carousel — the track when it has focus, and
       the arrows and dots, whose keydowns bubble up to here. The default is
       preventable and worth preventing: the browser would scroll the track by a
       few pixels, which a snap would then undo. */
    root.addEventListener("keydown", (event) => {
      if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return;
      event.preventDefault();
      goTo(pageAt() + (event.key === "ArrowRight" ? 1 : -1));
      start();
    });

    /* A swipe or a trackpad moves the track without going through goTo(). */
    let ticking = 0;
    track.addEventListener("scroll", () => {
      if (ticking) return;
      ticking = requestAnimationFrame(() => {
        ticking = 0;
        mark(pageAt());
      });
    }, { passive: true });

    /* A rotated phone crosses a breakpoint, so the cards per view and the dot
       count change with it. The card that was leading stays leading. Called by
       the one window `resize` listener below, not by a listener of its own. */
    function relayout() {
      const leading = step ? Math.round(track.scrollLeft / step) : 0;
      measure();
      buildDots();
      goTo(Math.floor(leading / perView), "instant");
      start();
    }

    measure();
    buildDots();
    /* Only now do the controls appear: without JS they would be three things
       that do nothing, so CSS keeps the row hidden until this class lands. */
    root.classList.add("is-ready");
    const car = { root: root, start: start, stop: stop, relayout: relayout };
    reviewsSweep();
    reviewsLive.add(car);
    reviewsGlobalsOnce();
    start();
    return car;
  }

  /* ---------- card photos (Task 25) -----------------------------------------
     A product's other photos, on the card itself. `snippets/product-card.liquid`
     renders them as a scroll-snapping track with a row of bars under it, and
     the swipe is the browser's own — see the CSS block of the same name. This
     is the three things the browser cannot do on its own: say which photo is
     showing, let a mouse through the set without a click, and make the gesture
     discoverable to a thumb that has not been told about it.

     Everything is delegated from `document` and nothing is bound per card. Half
     the catalogue has a track (92 of 195 products), a shop page shows two dozen
     of them, and the "You may also like" cards are fetched long after init()
     has run — a per-card binder would be a listener per card *and* a re-bind
     after every fetch. Four listeners for the whole page instead, and a card
     that did not exist when the page loaded behaves like one that did.

     Without JS none of this is missed: the track still swipes and still snaps.
     Only the bars stop moving, which is a hint gone quiet, not a feature lost. */
  const CARD_SWIPED = "sad:card-swiped";

  /* Private mode throws on both of these. A shopper who cannot be remembered
     simply gets the hint again on the next page, which is the right failure. */
  function cardSwipedAlready() {
    try {
      return sessionStorage.getItem(CARD_SWIPED) === "1";
    } catch (error) {
      return false;
    }
  }

  function rememberCardSwipe() {
    try {
      sessionStorage.setItem(CARD_SWIPED, "1");
    } catch (error) {
      /* nothing to remember it with */
    }
  }

  /* `instant`, never `auto`: `auto` resolves to whatever `scroll-behavior` is
     in force (the mock-up asks for `smooth` on the document), and a scrub that
     glided would trail the cursor by a photo. */
  function cardScrollTo(track, left) {
    if (track.scrollTo) track.scrollTo({ left: left, behavior: "instant" });
    else track.scrollLeft = left;
  }

  /* Which photo is showing is the scroll position rounded to a slide, read off
     the track rather than kept as a second copy of it — a finger moves the
     track without asking anyone. The bars are a sibling of the track, not a
     child, so they stay put while the photos slide. */
  function markCardPhoto(track) {
    const bars = track.parentElement ? $("[data-card-bars]", track.parentElement) : null;
    if (!bars || !track.clientWidth) return;
    const at = Math.round(track.scrollLeft / track.clientWidth);
    [...bars.children].forEach((bar, i) => bar.classList.toggle("is-active", i === at));
    /* Past the first photo, the shopper has found it. The hint below never
       shows again this visit. */
    if (at >= 1) rememberCardSwipe();
  }

  let cardGlobals = false;

  function cardPhotoGlobalsOnce() {
    if (cardGlobals) return;
    cardGlobals = true;

    /* `scroll` does not bubble, so a delegated listener has to be a capturing
       one — the capture phase is the only one that passes through `document` on
       its way down to the element. The frame is shared across tracks: a page
       has a dozen, a trackpad flick can move more than one, and one rAF for all
       of them is one read of the layout each rather than one per event. */
    const moved = new Set();
    let frame = 0;
    document.addEventListener("scroll", (event) => {
      const track = event.target;
      if (!track || !track.matches || !track.matches("[data-card-track]")) return;
      moved.add(track);
      if (frame) return;
      frame = requestAnimationFrame(() => {
        frame = 0;
        moved.forEach(markCardPhoto);
        moved.clear();
      });
    }, true);

    /* A mouse does not swipe. Moving it across the photo splits the photo into
       one zone per slide and shows that slide, so the whole set is one pass of
       the wrist and the shopper never leaves the grid. Mouse only: a finger's
       `pointermove` *is* the swipe, and `pointerType` is how the two are told
       apart — the same test the reviews carousel uses for its hover pause.

       It scrolls the track rather than moving anything itself, so the bars
       follow through the handler above and there is one answer to "which
       photo" whatever moved it. The card stays a link: nothing here calls
       `preventDefault`, so a click on the photo opens the product as always. */
    document.addEventListener("pointermove", (event) => {
      if (event.pointerType !== "mouse") return;
      const media = event.target.closest ? event.target.closest(".product-card__media") : null;
      if (!media) return;
      const track = $("[data-card-track]", media);
      if (!track) return;
      const slides = track.children.length;
      const box = media.getBoundingClientRect();
      if (slides < 2 || !box.width) return;
      const zone = Math.max(
        0,
        Math.min(slides - 1, Math.floor(((event.clientX - box.left) / box.width) * slides))
      );
      /* Only on a change of zone. A mouse fires this every few pixels, and
         re-scrolling a snapping track to where it already is fights the snap. */
      if (track.dataset.cardZone === String(zone)) return;
      track.dataset.cardZone = String(zone);
      cardScrollTo(track, zone * track.clientWidth);
    });

    /* Off the photo, back to the first one — the card goes back to looking like
       the card it was. `pointerout` fires for a finger too, once, as it lifts,
       and a shopper who has just swiped to photo 2 must not have it yanked
       back: so this is mouse-only as well. */
    document.addEventListener("pointerout", (event) => {
      if (event.pointerType !== "mouse") return;
      const media = event.target.closest ? event.target.closest(".product-card__media") : null;
      if (!media || (event.relatedTarget && media.contains(event.relatedTarget))) return;
      const track = $("[data-card-track]", media);
      if (!track) return;
      delete track.dataset.cardZone;
      cardScrollTo(track, 0);
    });

    /* The nudge comes off when it finishes — `animationend` bubbles up from the
       photo that ran it — and the instant a finger lands, so a shopper who is
       already swiping is never fighting an animation for the same track. */
    document.addEventListener("animationend", (event) => {
      const track = event.target.closest ? event.target.closest("[data-card-track]") : null;
      if (track) track.classList.remove("is-nudging");
    });

    document.addEventListener("pointerdown", (event) => {
      const track = event.target.closest ? event.target.closest("[data-card-track]") : null;
      if (track) track.classList.remove("is-nudging");
    });
  }

  /* The nudge. The bars say there are more photos; they do not say that the
     photo moves. So the first track a shopper actually has in front of them
     slides about a sixth of a photo and comes back, once — the CSS owns the
     movement, this owns "which track, and when".

     Once per page view (the flag below), once per visit (the session flag, set
     the moment any track reaches its second photo), never on a device with a
     real pointer, and never when less motion has been asked for. An
     IntersectionObserver rather than a scroll listener because it is the card's
     own geometry that decides; 60% is "being looked at" rather than "on
     screen", which on a phone is the difference between the card the shopper
     has scrolled to and the one below it. */
  let cardNudgeUsed = false;
  let cardNudgeEye = null;

  function cardNudgeWanted() {
    if (cardNudgeUsed || cardSwipedAlready()) return false;
    if (typeof window.matchMedia !== "function") return false;
    if (typeof IntersectionObserver !== "function") return false;
    if (!window.matchMedia("(hover: none) and (pointer: coarse)").matches) return false;
    return !window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  }

  function cardNudgeSeen(entries) {
    entries.forEach((entry) => {
      if (cardNudgeUsed || !entry.isIntersecting || entry.intersectionRatio < 0.6) return;
      cardNudgeUsed = true;
      if (cardNudgeEye) cardNudgeEye.disconnect();
      cardNudgeEye = null;
      const track = entry.target;
      /* A beat after it settles, not the moment it appears: a hint that moves
         while the shopper is still scrolling past is scenery. */
      setTimeout(() => {
        if (track.isConnected) track.classList.add("is-nudging");
      }, 400);
    });
  }

  /* The module's one entry point — `observe(root)` by another name, in this
     file's house style. Called for the document on load, for a section the
     editor re-renders, and for a freshly fetched recommendations band. */
  function cardPhotos(root) {
    cardPhotoGlobalsOnce();
    if (!cardNudgeWanted()) return;
    const tracks = $$("[data-card-track]", root && root.querySelectorAll ? root : document);
    if (!tracks.length) return;
    if (!cardNudgeEye) {
      cardNudgeEye = new IntersectionObserver(cardNudgeSeen, { threshold: 0.6 });
    }
    tracks.forEach((track) => cardNudgeEye.observe(track));
  }

  function init() {
    $$("[data-product-root]").forEach((root) => {
      gallery(root);
      zoom(root);
      variantPicker(root);
      measurements(root);
      fabDodge(root);
    });

    $$("form[data-product-form]").forEach((form) => {
      form.addEventListener("submit", (event) => {
        event.preventDefault();
        addToCart(form);
      });
    });

    /* The header pill is a link to /cart; with JS it opens the drawer instead —
       but only while there is a drawer to open, so a page that failed to render
       the section still has a working cart link. */
    $$("[data-drawer-open], [data-cart-count]").forEach((el) => {
      el.addEventListener("click", (event) => {
        if (!$("#cart-drawer")) return;
        event.preventDefault();
        openDrawer();
      });
    });

    askUs(document);
    cardPhotos(document);
    $$("[data-reviews-carousel]").forEach(reviewsCarousel);

    bindDrawer();
    bindCartPage();

    document.addEventListener("keydown", (event) => {
      if (event.key === "Escape") closeDrawer();
    });

    $$("[data-recommendations]").forEach(loadRecommendations);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }

  /* The theme editor re-renders one section at a time without reloading the
     page, which drops that section's handlers. Re-bind what the reloaded
     section owns. Editor-only; on the storefront this event never fires. */
  document.addEventListener("shopify:section:load", (event) => {
    const section = event.target;
    if (!section) return;
    $$("[data-product-root]", section).forEach((root) => {
      gallery(root);
      zoom(root);
      variantPicker(root);
      measurements(root);
      fabDodge(root);
    });
    $$("form[data-product-form]", section).forEach((form) => {
      form.addEventListener("submit", (submitEvent) => {
        submitEvent.preventDefault();
        addToCart(form);
      });
    });
    $$("[data-drawer-open], [data-cart-count]", section).forEach((el) => {
      el.addEventListener("click", (clickEvent) => {
        if (!$("#cart-drawer")) return;
        clickEvent.preventDefault();
        openDrawer();
      });
    });
    askUs(section);
    cardPhotos(section);
    $$("[data-reviews-carousel]", section).forEach(reviewsCarousel);
    bindDrawer();
    bindCartPage();
    $$("[data-recommendations]", section).forEach(loadRecommendations);
  });

  return {
    askUs,
    cardPhotos,
    fabDodge,
    reviewsCarousel,
    /* The live carousels, exposed so the Task 21 harness can assert that a
       theme-editor re-render leaves one and not a pile. */
    reviewsLive,
    variantPicker,
    measurements,
    gallery,
    zoom,
    addToCart,
    changeLine,
    refreshCart,
    refreshCartPage,
    openDrawer,
    closeDrawer,
  };
})();
